import { Button, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { FieldError, SubmitHandler, UseFormReturn, useFieldArray, useForm } from 'react-hook-form';
import { Box, Stack } from '@mui/system';
import { ControlledField, FormControlGroup, Offer, RideOfferType, colors } from 'remote/global';
import { CheckCircle } from '@mui/icons-material';
import { useOperatorCurrency } from '@/api/queries/users';
import { rideOffersSchema, RideOffersSchemaType } from './types';
import { EndAdornment } from '../form-helpers';

interface OfferCardProps {
  form: UseFormReturn<RideOffersSchemaType>;
  tripIndex: number;
  offerIndex: number;
  offer: Offer;
  type: RideOfferType;
  refNumber: string;
  error: boolean;
  isCreateOfferVariant?: boolean;
}

function OfferCard({
  form,
  tripIndex,
  offerIndex,
  offer,
  type,
  refNumber,
  error,
  isCreateOfferVariant,
}: OfferCardProps) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const currency = useOperatorCurrency();
  const { control, setValue, watch, trigger, register, getValues } = form;
  const operatorOfferUuids = watch('operatorOfferUuids') ?? [];
  const isSelected = operatorOfferUuids.includes(offer.uuid);
  const priceRegister = register(
    `trips.${tripIndex}.operatorOffers.${offerIndex}.operatorPriceExclVat`
  );
  // get rid of orig ref because of forwardRef error issue
  const { ref, ...restPriceRegister } = priceRegister;

  const handleSetIncludingVatPrice = () => {
    const { operatorPriceExclVat } = getValues(`trips.${tripIndex}.operatorOffers.${offerIndex}`);
    if (operatorPriceExclVat != null)
      setValue(
        `trips.${tripIndex}.operatorOffers.${offerIndex}.operatorPriceExclVat`,
        operatorPriceExclVat
      );
  };

  const getBorder = () => {
    if (error) return `1px solid ${colors.busyRed}`;
    if (isSelected) return `1px solid ${colors.uiBlue600}`;
    return `1px solid transparent`;
  };

  return (
    <Paper
      elevation={3}
      data-testid={`choose-offer-${offerIndex}`}
      data-testbookingnumber={refNumber}
      onClick={() => {
        // For 'isCreateOfferVariant' we allow to select multiple
        if (isCreateOfferVariant) {
          if (isSelected)
            setValue(
              'operatorOfferUuids',
              operatorOfferUuids.filter((uuid) => uuid !== offer.uuid)
            );
          else setValue('operatorOfferUuids', [...operatorOfferUuids, offer.uuid]);
        } else {
          // Only allow to select one
          setValue('operatorOfferUuids', [offer.uuid]);
        }

        // Triggers revalidation
        if (error) trigger('operatorOfferUuids');
      }}
      sx={{
        display: 'flex',
        flex: 1,
        position: 'relative',
        minWidth: 300,
        maxWidth: 300,
        padding: '24px',
        background: isSelected ? colors.tiffanyBlue5 : 'white',
        cursor: isSelected ? 'auto' : 'pointer',
        border: getBorder(),
      }}
    >
      {isSelected && (
        <CheckCircle
          sx={{
            position: 'absolute',
            background: 'white',
            color: colors.uiBlue600,
            right: -10,
            top: -10,
            zIndex: 99,
          }}
        />
      )}
      <Box display='flex' flexDirection='column' width='100%'>
        <Box display='flex' justifyContent='space-between' alignItems='flex-start' gap={2}>
          <Box display='flex' flexDirection='column'>
            <Typography
              sx={{
                color: colors.uiBlue600,
                fontSize: '24px',
                fontWeight: 600,
                lineHeight: '32px',
              }}
            >
              {offer.vehicleMake}
            </Typography>
            <Typography
              sx={{
                color: colors.darkBlue,
                fontSize: '12px',
                lineHeight: '18px',
              }}
            >
              {t('terms.attributes.vehicle.orSimilar.label')}
            </Typography>
          </Box>
          <Box
            sx={{
              border: `1px solid ${colors.uiBlue600}`,
              borderRadius: '6px',
              padding: '4px 8px',
              color: colors.uiBlue600,
              fontSize: '12px',
              textTransform: 'uppercase',
              fontWeight: 700,
            }}
          >
            {t(`TripType.${type}`)}
          </Box>
        </Box>
        <Box margin='15px 0'>
          <Typography display='flex' lineHeight='24px'>
            <span
              style={{
                fontSize: '16px',
                color: colors.gray1,
                marginRight: '5px',
              }}
            >
              {t('terms.attributes.vehicle.class')}
            </span>
            <span
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: colors.headerBlack,
              }}
            >
              {t(`VehicleClass.${offer.vehicleClass}`)}
            </span>
          </Typography>
          <Typography display='flex' alignItems='center' lineHeight='24px'>
            <span
              style={{
                fontSize: '16px',
                color: colors.gray1,
                marginRight: '5px',
              }}
            >
              {t('terms.attributes.vehicle.seats')}
            </span>
            <span
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: colors.headerBlack,
              }}
            >
              {offer.vehicleSeats}
            </span>
          </Typography>
        </Box>
        <ControlledField
          control={control}
          {...restPriceRegister}
          fieldProps={{
            ref: priceRegister.ref,
            inputProps: {
              onChange: (event) => {
                restPriceRegister.onChange(event);

                // Handle calculation of price with VAT
                (async () =>
                  new Promise<void>((resolve) => {
                    resolve();
                  }).then(() => {
                    handleSetIncludingVatPrice();
                  }))();
              },
            },
            onClick: (e) => {
              e.stopPropagation(); // Makes sure not to uncheck the selection
            },
            label: t('terms.attributes.price.exclVat'),
            sx: { maxWidth: 150, pointerEvents: isSelected ? 'auto' : 'none', mt: 'auto' },
            disabled: !isSelected,
            InputProps: {
              endAdornment: <EndAdornment text={currency} />,
              sx: { maxWidth: 125 },
            },
          }}
        />
      </Box>
    </Paper>
  );
}

function SelectRideOfferForm({
  onValid,
  onBack,
  defaultValues,
  isCreateOfferVariant,
}: {
  onValid: SubmitHandler<RideOffersSchemaType>;
  onBack: (data: RideOffersSchemaType) => void;
  defaultValues: RideOffersSchemaType | undefined;
  isCreateOfferVariant?: boolean;
}) {
  const tInstance = useTranslation();
  const { t } = tInstance;
  const formInstance = useForm<RideOffersSchemaType>({
    defaultValues: {
      ...defaultValues,
    },
    resolver: zodResolver(rideOffersSchema),
  });
  const {
    handleSubmit,
    getValues,
    control,
    watch,
    formState: { isSubmitting, isLoading, errors },
  } = formInstance;
  const refNumber = watch('refNumber');

  const { fields } = useFieldArray({
    name: 'trips',
    keyName: 'fieldPropertyId',
    control,
  });

  if (defaultValues?.trips == null) return null;

  return (
    <form onSubmit={handleSubmit(onValid)} style={{ width: '100%' }}>
      <Grid item xs={12}>
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: '30px',
            fontWeight: 600,
            lineHeight: '38px',
            marginBottom: '24px',
          }}
        >
          {t('OffersView.title')}
        </Typography>
        <FormControlGroup error={errors?.operatorOfferUuids as FieldError} required>
          <Grid
            container
            item
            xs={12}
            gap={2}
            data-testId='offers-container'
            sx={{
              display: 'inline-flex',
              flexWrap: 'nowrap',
              overflow: 'auto',
              p: '1rem',
            }}
          >
            {fields.map((trip, tripIndex: number) => {
              return trip.operatorOffers.map((offer, offerIndex) => {
                return (
                  <OfferCard
                    isCreateOfferVariant={isCreateOfferVariant}
                    key={offer.uuid}
                    offer={offer as any}
                    type={trip.type}
                    refNumber={refNumber}
                    form={formInstance}
                    tripIndex={tripIndex}
                    offerIndex={offerIndex}
                    error={Boolean(errors.operatorOfferUuids)}
                  />
                );
              });
            })}
          </Grid>
        </FormControlGroup>
      </Grid>
      <Stack direction='row' justifyContent='space-between' spacing={1} my='2rem'>
        <Button
          type='button'
          onClick={() => onBack(getValues())}
          variant='outlined'
          disabled={isSubmitting || isLoading}
        >
          {t('back')}
        </Button>
        <Button
          type='submit'
          disabled={isSubmitting || isLoading}
          variant='contained'
          sx={{ display: 'flex', maxWidth: 300 }}
        >
          {t('continue')}
        </Button>
      </Stack>
    </form>
  );
}

export default SelectRideOfferForm;
